import React, { FormEvent, JSX, useEffect, useState } from 'react';
import cep from 'cep-promise';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import useForm from '@/hooks/useForm';
import validators from '@/helpers/validators';
import { updateMask as updateMaskCPF } from '@/helpers/masks/cpf';
import { updateMask as updateMaskPhone } from '@/helpers/masks/mobilePhone';
import { updateMask as updateMaskCEP } from '@/helpers/masks/cep';
// import { unmask } from '@/helpers/masks/generalDate';
import { getErrorMessage } from '@/helpers/error';

import { setLoading, useLoading } from '@/redux/loading/loadingSlice';
import { api } from '@/services/api';

import { Address } from '@/model/Address';
import ClientBasic from '@/model/ClientBasic';
import Client from '@/model/Client';
import { FirstLogin, FormInputNameRegister } from '@/features/register/type';
import {
  formatContactConfirmationDestination,
  validateRegisterFields,
} from '@/features/register/utils';

import { BiometricFaceValid } from '@/features/core/auth/components/BiometricFaceValid';
import { PostMessageRequest } from '@/model/PostMessageRequest';
import { PostMessageResponse } from '@/model/PostMessageResponse';
import { PostMessageRequestOrigin } from '@/model/PostMessageRequestOrigin';
import { REACT_APP_BIOMETRIC_KEY } from '@/utils/config';
import ConfirmAccount from '@/model/ConfirmAccount';
import ContactConfirmationResponse from '@/model/ContactConfirmationResponse';

import { RegisterUI } from '@/features/register/screen/ui';
import { Button, Modal } from '@/components';
import { AuthWrapper } from '@/features/core/auth/screens/AuthWrapper';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { loginClient } from '@/helpers/auth';
import { TermsContent } from '@/features/core/auth/components';
import { consumePendingPurchase, getPendingPurchase } from '@/helpers/navigation/pendingPurchase';

enum RegisterFlowStep {
  BIOMETRIC = 'BIOMETRIC',
  REGISTER = 'REGISTER',
  // CONFIRM_WHATSAPP = 'CONFIRM_WHATSAPP',
  SUCCESS = 'SUCCESS',
}

export const RegisterScreen: React.FC = (): JSX.Element => {
  const [shouldShowPasswordToText, setShouldShowPasswordToText] = useState(false);
  const [shouldShowPasswordToText2, setShouldShowPasswordToText2] = useState(false);
  const [firstLogin, setFirstLogin] = useState<FirstLogin>();
  const [flowStep, setFlowStep] = useState<RegisterFlowStep>(RegisterFlowStep.BIOMETRIC);
  const [biometricData, setBiometricData] = useState<PostMessageResponse>();
  const [biometricRequest, setBiometricRequest] = useState<PostMessageRequest>();
  const [temporaryPersonIdentification] = useState(() => crypto.randomUUID());
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const registerState = location.state as { cpf?: string; name?: string } | null;
  const cpfFromState = registerState?.cpf?.trim() || '';
  const nameFromState = registerState?.name?.trim() || '';
  const cpfFromQuery = searchParams.get('cpf')?.trim() || '';
  const cpfInitialValue = cpfFromState || cpfFromQuery;
  const [isTermsVisible, setIsTermsVisible] = useState(false);
  const [registrationError, setRegistrationError] = useState<string>();
  const [contactConfirmationDestination, setContactConfirmationDestination] = useState<string>();
  const [contactConfirmationError, setContactConfirmationError] = useState<string>();
  const navigate = useNavigate();
  const pendingPurchase = getPendingPurchase();

  const dispatch = useDispatch();
  const { loading } = useSelector(useLoading);

  const {
    formData: formDataRegister,
    formErrors: formErrorsRegister,
    setErrors: setErrorsRegister,
    setFormErrors: setFormErrorsRegister,
    onChangeFormInput: onChangeFormInputRegister,
    runAllValidators: runAllRegisterValidators,
  } = useForm({
    initialData: {
      name: '',
      document: '',
      email: '',
      phone: '',
      confirmationCode: '',
      // date: '',
      // motherName: '',
      zipCode: '',
      state: '',
      city: '',
      district: '',
      street: '',
      complement: '',
      addressnumber: '',
      password: '',
      confirmPassword: '',
      terms: '',
    },
    validators: {
      name: [validators.required],
      document: [validators.required, validators.cpf],
      email: [validators.required, validators.email],
      phone: [validators.required, validators.mobilePhone],
      confirmationCode: [validators.required],
      // date: [validators.required, validators.birthday, validators.maxLength(10)],
      // motherName: [validators.required],
      zipCode: [validators.required],
      state: [validators.required],
      city: [validators.required],
      district: [validators.required],
      street: [validators.required],
      password: [
        validators.required,
        validators.minLength(8),
        validators.maxLength(15),
        validators.hasPasswordOnlyNumberCharacteres,
      ],
      confirmPassword: [
        validators.required,
        validators.minLength(8),
        validators.maxLength(15),
        validators.hasPasswordOnlyNumberCharacteres,
      ],
      terms: [validators.required],
    },
    formatters: {
      document: updateMaskCPF,
      phone: updateMaskPhone,
      // date: updateMaskDate,
      zipCode: updateMaskCEP,
    },
  });

  const handleValidateRegisterFields = (fields: FormInputNameRegister[]): boolean =>
    validateRegisterFields({
      fields,
      formData: formDataRegister,
      formErrors: formErrorsRegister,
      runAllValidators: runAllRegisterValidators,
      setFormErrors: setFormErrorsRegister,
    });

  // const {
  //   formData: formDataConfirmEmail,
  //   formErrors: formErrorsConfirmEmail,
  //   onChangeFormInput: onChangeFormInputConfirmEmail,
  //   isFormValid: isFormValidConfirmEmail,
  // } = useForm({
  //   initialData: {
  //     cpf: '',
  //     confirmationCode: '',
  //   },
  //   validators: {
  //     cpf: [validators.required, validators.cpf],
  //     confirmationCode: [validators.required],
  //   },
  //   formatters: {
  //     cpf: updateMaskCPF,
  //   },
  // });

  const handleSendContactConfirmation = async (): Promise<boolean> => {
    try {
      dispatch(setLoading(true));
      setContactConfirmationError(undefined);

      const { data } = await api.post<ContactConfirmationResponse>(
        '/client/email-phone/confirm/send',
        {
          cpf: formDataRegister[FormInputNameRegister.document],
          email: formDataRegister[FormInputNameRegister.email],
          phone: formDataRegister[FormInputNameRegister.phone],
        },
      );

      setContactConfirmationDestination(
        formatContactConfirmationDestination(
          data.channel,
          formDataRegister[FormInputNameRegister.email],
          formDataRegister[FormInputNameRegister.phone],
        ),
      );
      onChangeFormInputRegister(FormInputNameRegister.confirmationCode)('');

      toast.success('Código de confirmação enviado!');
      return true;
    } catch (error) {
      setContactConfirmationError(
        getErrorMessage(error) || 'Não foi possível enviar o código de confirmação.',
      );

      return false;
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleConfirmContact = async (): Promise<boolean> => {
    try {
      dispatch(setLoading(true));

      await api.post('/client/email/confirm', {
        email: formDataRegister[FormInputNameRegister.email],
        phone: formDataRegister[FormInputNameRegister.phone],
        confirmationCode: formDataRegister[FormInputNameRegister.confirmationCode],
      });

      toast.success('Contato confirmado com sucesso!');
      return true;
    } catch (error) {
      setFormErrorsRegister({
        ...formErrorsRegister,
        confirmationCode: [getErrorMessage(error) || 'Código de confirmação inválido.'],
      });

      return false;
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleValidatePersonalData = async (): Promise<boolean> => {
    const cpf = formDataRegister[FormInputNameRegister.document];

    if (!biometricData) {
      toast.error('Realize a validação facial para continuar o cadastro.');
      setFlowStep(RegisterFlowStep.BIOMETRIC);
      return false;
    }

    try {
      dispatch(setLoading(true));
      await api.get(`/client/confirm/check-exist-biometric/${cpf}`);

      const payload: ConfirmAccount = {
        cpf,
        faceId: biometricData.faceId,
        confidence: biometricData.confidence,
        faceImage: biometricData.imageBase64,
      };

      await api.post('/client/confirmation-account', payload);
      return true;
    } catch (error) {
      setFormErrorsRegister({
        ...formErrorsRegister,
        document: [
          getErrorMessage(error) || 'Falha ao validar dados pessoais, tente novamente mais tarde',
        ],
      });
      return false;
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleToggleTerms = (): void => {
    setIsTermsVisible(prev => !prev);
  };

  const handleOnTogglePasswordToText = (): void => {
    setShouldShowPasswordToText(prev => !prev);
  };

  const handleOnTogglePasswordToText2 = (): void => {
    setShouldShowPasswordToText2(prev => !prev);
  };

  const handleOnSubmitRegister = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setRegistrationError(undefined);

    if (!biometricData) {
      toast.error('Realize a validação facial para continuar o cadastro.');
      setFlowStep(RegisterFlowStep.BIOMETRIC);
      return;
    }
    try {
      dispatch(setLoading(true));

      // const dateString = unmask(formDataRegister[FormInputNameRegister.date]);

      // if (!dateString) {
      //   setErrorsRegister({
      //     [FormInputNameRegister.date]: ['Data de nascimento inválida!'],
      //   });
      //   return;
      // }

      if (
        formDataRegister[FormInputNameRegister.password] !==
        formDataRegister[FormInputNameRegister.confirmPassword]
      ) {
        setErrorsRegister({
          [FormInputNameRegister.password]: ['Senhas divergentes!'],
          [FormInputNameRegister.confirmPassword]: ['Senhas divergentes!'],
        });
        setRegistrationError('As senhas não conferem. Volte à etapa de senha para corrigir.');
        return;
      }

      // const date = new Date(`${dateString}T12:00:00.000Z`);

      const address = {
        zipCode: formDataRegister[FormInputNameRegister.zipCode],
        state: formDataRegister[FormInputNameRegister.state],
        city: formDataRegister[FormInputNameRegister.city],
        district: formDataRegister[FormInputNameRegister.district],
        street: formDataRegister[FormInputNameRegister.street],
        complement: formDataRegister[FormInputNameRegister.complement],
        number: formDataRegister[FormInputNameRegister.addressnumber],
      } as Address;

      const payload: ClientBasic = {
        name: formDataRegister[FormInputNameRegister.name],
        cpf: formDataRegister[FormInputNameRegister.document],
        email: formDataRegister[FormInputNameRegister.email],
        cellPhone: formDataRegister[FormInputNameRegister.phone],
        // birthDate: date,
        // motherName: formDataRegister[FormInputNameRegister.motherName],
        password: formDataRegister[FormInputNameRegister.password],
        address,
        acceptedTerms:
          !!formDataRegister[FormInputNameRegister.terms] &&
          formDataRegister[FormInputNameRegister.terms] === 'true',
        confirmationCode: formDataRegister[FormInputNameRegister.confirmationCode],
      };

      const { data } = await api.post<Client>('/client', payload);

      const credentials = {
        cpf: data.cpf,
        password: formDataRegister[FormInputNameRegister.password],
      };

      setFirstLogin(credentials);

      try {
        await loginClient(credentials.cpf, credentials.password);
      } catch {
        toast.info('Cadastro concluído. Faça login para continuar.');
      }

      const purchasePath = consumePendingPurchase();

      if (purchasePath) {
        navigate(purchasePath, { replace: true });
        return;
      }

      setFlowStep(RegisterFlowStep.SUCCESS);
    } catch (error) {
      setRegistrationError(
        getErrorMessage(error) || 'Falha ao realizar cadastro. Tente novamente mais tarde.',
      );
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleOnChangeCEP = async (value: string): Promise<void> => {
    if (value.length !== 9) return;
    const cepResponse = await cep(value);
    onChangeFormInputRegister(FormInputNameRegister.state)(cepResponse.state);
    onChangeFormInputRegister(FormInputNameRegister.city)(cepResponse.city);
    onChangeFormInputRegister(FormInputNameRegister.district)(cepResponse.neighborhood);
    onChangeFormInputRegister(FormInputNameRegister.street)(cepResponse.street);
  };

  const handleGoToLogin = (): void => {
    navigate(consumePendingPurchase() || '/');
  };

  const handleBiometricComplete = (data: PostMessageResponse): void => {
    setBiometricData(data);
    setFlowStep(RegisterFlowStep.REGISTER);
  };

  const getOsInformation = (): string => {
    const { userAgent } = window.navigator;
    let os = 'Unknown OS';

    if (userAgent.includes('Win')) {
      os = 'Windows';
    } else if (userAgent.includes('Mac')) {
      os = 'MacOS';
    } else if (userAgent.includes('Linux')) {
      os = 'Linux';
    } else if (userAgent.includes('Android')) {
      os = 'Android';
    } else if (userAgent.includes('iOS')) {
      // For iOS devices (iPhone, iPad, iPod)
      os = 'iOS';
    }
    return os;
  };

  const getBrowserInformation = (): string => {
    const { userAgent } = window.navigator;
    let browserName;

    if (userAgent.indexOf('Edg') > -1) {
      browserName = 'Microsoft Edge';
    } else if (userAgent.indexOf('Chrome') > -1) {
      browserName = 'Chrome';
    } else if (userAgent.indexOf('Firefox') > -1) {
      browserName = 'Firefox';
    } else if (userAgent.indexOf('Safari') > -1) {
      browserName = 'Safari';
    } else if (userAgent.indexOf('Opera') > -1 || userAgent.indexOf('Opr') > -1) {
      // Opr for newer Opera versions
      browserName = 'Opera';
    } else if (userAgent.indexOf('Trident') > -1 || userAgent.indexOf('MSIE') > -1) {
      browserName = 'Internet Explorer';
    } else {
      browserName = 'Unknown';
    }
    return browserName;
  };

  useEffect(() => {
    if (flowStep !== RegisterFlowStep.BIOMETRIC) return undefined;

    const timer = window.setTimeout(() => {
      setBiometricRequest({
        apiKey: String(REACT_APP_BIOMETRIC_KEY),
        company: 'Banca Do Ingresso e Eventos LTDA',
        personIdentification: temporaryPersonIdentification,
        origin: PostMessageRequestOrigin.BROWSER,
        systemOS: getOsInformation(),
        browser: getBrowserInformation(),
        requestUrl: window.location.origin,
      });
    }, 300);

    return () => window.clearTimeout(timer);
  }, [flowStep, temporaryPersonIdentification]);

  useEffect(() => {
    if (cpfInitialValue && formDataRegister[FormInputNameRegister.document] !== cpfInitialValue) {
      onChangeFormInputRegister(FormInputNameRegister.document)(cpfInitialValue);
    }

    if (nameFromState && formDataRegister[FormInputNameRegister.name] !== nameFromState) {
      onChangeFormInputRegister(FormInputNameRegister.name)(nameFromState);
    }
  }, [cpfInitialValue, nameFromState, formDataRegister.document, formDataRegister.name]);

  if (flowStep === RegisterFlowStep.BIOMETRIC) {
    return (
      <AuthWrapper isHomepage={false}>
        <div className="min-h-screen bg-gray-50">
          <Modal
            visible={flowStep === RegisterFlowStep.BIOMETRIC}
            onSetVisible={() => undefined}
            title=""
            showCloseButton={false}
            disableCloseOutside={true}
          >
            <BiometricFaceValid
              request={biometricRequest}
              onAnalysisComplete={handleBiometricComplete}
            />
          </Modal>
        </div>
      </AuthWrapper>
    );
  }

  if (flowStep === RegisterFlowStep.SUCCESS) {
    return (
      <AuthWrapper isHomepage={false}>
        <div className="min-h-screen bg-gray-50 py-10 px-4">
          <div className="mx-auto w-full max-w-4xl">
            <div className="mb-8 text-center">
              <h1 className="text-3xl font-bold text-gray-900">Cadastro concluído</h1>
              <p className="mt-2 text-sm text-gray-600">
                Sua conta foi criada e confirmada com sucesso.
              </p>
            </div>

            <div className="mx-auto w-full max-w-2xl rounded-2xl bg-white shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-8 md:px-8 md:py-10 text-center">
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-green-100 text-green-700 text-2xl font-bold">
                  ✓
                </div>

                <h2 className="text-2xl font-bold text-gray-900">Tudo certo!</h2>

                <p className="mt-2 text-sm text-gray-600">
                  Seu contato foi confirmado e seu cadastro foi concluído com sucesso.
                </p>

                {firstLogin?.cpf && (
                  <div className="mt-6 rounded-xl border border-gray-200 bg-gray-50 p-4">
                    <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                      CPF cadastrado
                    </p>
                    <p className="mt-1 text-base font-semibold text-gray-900">
                      {updateMaskCPF(firstLogin.cpf)}
                    </p>
                  </div>
                )}

                <div className="mt-8 flex justify-center">
                  <Button
                    type="button"
                    title={pendingPurchase ? 'Continuar compra' : 'Continuar'}
                    buttonStyle="primary"
                    size="xlg"
                    className="w-full md:w-[220px]"
                    onClick={handleGoToLogin}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </AuthWrapper>
    );
  }
  return (
    <>
      <AuthWrapper isHomepage={false}>
        <RegisterUI
          state={loading}
          formData={formDataRegister}
          formErrors={formErrorsRegister}
          onChangeFormInput={onChangeFormInputRegister}
          onChangeCEP={handleOnChangeCEP}
          shouldShowPasswordToText={shouldShowPasswordToText}
          shouldShowPasswordToText2={shouldShowPasswordToText2}
          onTogglePasswordToText={handleOnTogglePasswordToText}
          onTogglePasswordToText2={handleOnTogglePasswordToText2}
          onSubmit={handleOnSubmitRegister}
          onToggleTerms={handleToggleTerms}
          onGoToLogin={handleGoToLogin}
          isTermsVisible={isTermsVisible}
          termsContent={<TermsContent />}
          onValidatePersonalData={handleValidatePersonalData}
          onSendContactConfirmation={handleSendContactConfirmation}
          onConfirmContact={handleConfirmContact}
          onValidateFields={handleValidateRegisterFields}
          registrationError={registrationError}
          contactConfirmationDestination={contactConfirmationDestination}
          contactConfirmationError={contactConfirmationError}
          onResendContactConfirmation={handleSendContactConfirmation}
        />
      </AuthWrapper>
    </>
  );
};
